/* eslint-disable no-constant-condition */
import { call, put, fork, all, takeEvery, select } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import schema from './schema';

import * as examActions from './actions';
import * as API from '../api';
import { getResponse } from './selectors';
import { QUESTION_GROKKED } from '../constants';

// Saga rules of thumb
// Sagas are generators. they pause execution and continue under differing circumstances
// "take" waits to receive a dispatched *signal* action, - intended for the components to trigger sagas
// "put" immediately dispatches a *message* action with optional data - intended for the reducer
// "call" performs an async function. and continues execution when it is resolved.
// yield* passes execution over to the other generator.

/** Subroutines **/

export function* createSitting(examId) {
  yield put(examActions.createSitting.request(examId));
  const response = yield call(API.createSitting, examId);
  if (response.ok) {
    const examSitting = normalize(response.body, schema.sitting);
    yield put(examActions.createSitting.success(examSitting));
  } else {
    yield put(examActions.createSitting.failure(response.failureReason));
  }
}

export function* ensureSittingLoaded({ payload: { examId } }) {
  yield put(examActions.loadSitting.request(examId));
  const response = yield call(API.loadSitting, examId);

  if (response.ok && response.status === 200) {
    const examSitting = normalize(response.body, schema.sitting);
    yield put(examActions.loadSitting.success(examSitting));
  } else if (response.ok && response.status === 204) {
    // if no sitting exists.. create it instead
    yield call(createSitting, examId);
  } else {
    yield put(examActions.loadSitting.failure(response.failureReason));
  }
}

export function* loadExam({ payload: { examId, isDemo } }) {
  yield put(examActions.loadExam.request(examId));
  const response = yield call(API.loadExam, examId, isDemo);
  if (response.ok) {
    const exam = normalize(response.body, schema.exam);
    yield put(examActions.loadExam.success(examId, exam));
  } else {
    yield put(
      examActions.loadExam.failure(
        examId,
        response.status,
        response.failureReason
      )
    );
  }
}

export function* loadExamSolution({ payload: { examId } }) {
  yield put(examActions.loadExamSolution.request(examId));
  const response = yield call(API.loadExamSolution, examId);
  if (response.ok) {
    const solutionsByQuestionId = normalize(
      response.body,
      schema.completeExamSolution
    );
    yield put(examActions.loadExamSolution.success(solutionsByQuestionId));
  } else {
    yield put(examActions.loadExamSolution.failure(response.failureReason));
  }
}

export function* loadClassQuestionResponses({
  payload: { classId, examId, questionId },
}) {
  yield put(
    examActions.loadClassQuestionResponses.request(classId, examId, questionId)
  );
  const response = yield call(
    API.loadClassQuestionResponses,
    classId,
    examId,
    questionId
  );
  if (response.ok) {
    yield put(
      examActions.loadClassQuestionResponses.success(
        classId,
        examId,
        questionId,
        response.body.responses
      )
    );
  } else {
    yield put(
      examActions.loadClassQuestionResponses.failure(response.failureReason)
    );
  }
}

export function* loadClassExamSittings({ payload: { classId, examId } }) {
  yield put(examActions.loadClassExamSittings.request(classId, examId));

  const response = yield call(API.loadClassExamSittings, classId, examId);

  if (response.ok) {
    yield put(
      examActions.loadClassExamSittings.success(
        classId,
        examId,
        response.body.get('sittings')
      )
    );
  } else {
    yield put(
      examActions.loadClassExamSittings.failure(response.failureReason)
    );
  }
}

export function* fetchClassQuestionSetAggregates({
  payload: { examId, classId },
}) {
  yield put(
    examActions.fetchClassQuestionSetAggregates.request(examId, classId)
  );

  const response = yield call(
    API.fetchClassQuestionSetAggregates,
    examId,
    classId
  );

  if (response.ok) {
    const aggregateData = response.body;
    yield put(
      examActions.fetchClassQuestionSetAggregates.success(
        examId,
        classId,
        aggregateData
      )
    );
  } else {
    yield put(
      examActions.fetchClassQuestionSetAggregates.failure(
        examId,
        classId,
        response.failureReason
      )
    );
  }
}

export function* submitQuestionResponse({
  payload: { examId, questionId, sittingId, typeSpecificData },
}) {
  yield put(
    examActions.submitQuestionResponse.request(
      examId,
      questionId,
      sittingId,
      typeSpecificData
    )
  );
  const response = yield call(
    API.submitQuestionResponse,
    questionId,
    sittingId,
    typeSpecificData
  );
  if (response.ok) {
    // TODO remove normalizer. It just keys it by question id.
    const questionSittingResponse = normalize(response.body, schema.response);
    yield put(
      examActions.submitQuestionResponse.success(
        examId,
        questionId,
        questionSittingResponse
      )
    );
  } else {
    yield put(
      examActions.submitQuestionResponse.failure(
        examId,
        questionId,
        response.failureReason
      )
    );
  }
}

export function* submitQuestionGrok({
  payload: { examId, questionId, sittingId, grokked },
}) {
  // TODO any restrictions on submitting grok?
  const currentResponse = yield select(getResponse, questionId);
  const currentGrokValue =
    currentResponse?.get('grokked') || QUESTION_GROKKED.NO_RESPONSE;
  yield put(
    examActions.submitQuestionGrok.request(
      examId,
      questionId,
      sittingId,
      grokked
    )
  );
  const response = yield call(
    API.submitQuestionGrokked,
    questionId,
    sittingId,
    grokked
  );

  if (response.ok) {
    const questionSittingResponse = normalize(response.body, schema.response);
    yield put(
      examActions.submitQuestionGrok.success(
        examId,
        questionId,
        questionSittingResponse
      )
    );
  } else {
    yield put(
      examActions.submitQuestionGrok.failure(
        questionId,
        currentGrokValue,
        response.failureReason
      )
    );
  }
}

export function* submitQuestionMark({
  payload: { questionId, sittingId, mark },
}) {
  const currentResponse = yield select(getResponse, questionId);
  const currentMarkValue =
    currentResponse?.get('text_marks_from_student') || null;
  yield put(
    examActions.submitQuestionMark.request(questionId, sittingId, mark)
  );
  const response = yield call(
    API.submitQuestionMark,
    questionId,
    sittingId,
    mark
  );

  if (response.ok) {
    const questionSittingResponse = normalize(response.body, schema.response);
    yield put(
      examActions.submitQuestionMark.success(
        questionId,
        questionSittingResponse
      )
    );
  } else {
    yield put(
      examActions.submitQuestionMark.failure(
        questionId,
        currentMarkValue,
        response.failureReason
      )
    );
  }
}

export function* submitChecklistReflectionItem({
  payload: { questionId, sittingId, checklistReflectionItem },
}) {
  yield put(
    examActions.submitChecklistReflectionItem.request(
      questionId,
      sittingId,
      checklistReflectionItem
    )
  );

  const response = yield call(
    API.submitChecklistReflectionItem,
    questionId,
    sittingId,
    checklistReflectionItem
  );

  if (response.ok) {
    const questionSittingResponse = normalize(response.body, schema.response);
    yield put(
      examActions.submitChecklistReflectionItem.success(
        questionId,
        questionSittingResponse
      )
    );
  } else {
    yield put(
      examActions.submitChecklistReflectionItem.failure(response.failureReason)
    );
  }
}

/** Watchers **/
export function* watchLoadExam() {
  yield takeEvery(examActions.LOAD_EXAM_SIGNAL, loadExam);
}

export function* watchLoadClassExamSittings() {
  yield takeEvery(
    examActions.LOAD_CLASS_EXAM_SITTINGS_SIGNAL,
    loadClassExamSittings
  );
}

export function* watchFetchClassQuestionSetAggregates() {
  yield takeEvery(
    examActions.FETCH_CLASS_LESSON_QUESTION_AGGREGATES_SIGNAL,
    fetchClassQuestionSetAggregates
  );
}

export function* watchEnsureSittingLoaded() {
  yield takeEvery(
    examActions.ENSURE_SITTING_LOADED_SIGNAL,
    ensureSittingLoaded
  );
}

export function* watchLoadExamSolution() {
  yield takeEvery(examActions.LOAD_EXAM_SOLUTION_SIGNAL, loadExamSolution);
}

export function* watchClassQuestionResponses() {
  yield takeEvery(
    examActions.LOAD_CLASS_QUESTION_RESPONSES_SIGNAL,
    loadClassQuestionResponses
  );
}

export function* watchSubmitQuestionResponse() {
  yield takeEvery(
    examActions.SUBMIT_QUESTION_RESPONSE_SIGNAL,
    submitQuestionResponse
  );
}

export function* watchSubmitQuestionGrok() {
  yield takeEvery(examActions.SUBMIT_QUESTION_GROK_SIGNAL, submitQuestionGrok);
}

export function* watchSubmitQuestionMark() {
  yield takeEvery(examActions.SUBMIT_QUESTION_MARK_SIGNAL, submitQuestionMark);
}

export function* watchSubmitChecklistReflectionItem() {
  yield takeEvery(
    examActions.SUBMIT_CHECKLIST_REFLECTION_SIGNAL,
    submitChecklistReflectionItem
  );
}

// should this be a single saga with switch instead of forks? overheads?
export default function* examSagas() {
  yield all([
    fork(watchEnsureSittingLoaded),
    fork(watchLoadExam),
    fork(watchLoadClassExamSittings),
    fork(watchFetchClassQuestionSetAggregates),
    fork(watchLoadExamSolution),
    fork(watchClassQuestionResponses),
    fork(watchSubmitQuestionResponse),
    fork(watchSubmitQuestionGrok),
    fork(watchSubmitQuestionMark),
    fork(watchSubmitChecklistReflectionItem),
  ]);
}
